import React, { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Question, Step, UserResponse } from '../../types';

interface QuestionDetailSidebarProps {
  question: Question | null;
  step: Step | null;
  userResponses: UserResponse[];
  questionOrder: number;
  onClose: () => void;
  onAddResponse?: (questionId: number, responseText: string, interval: string) => Promise<void>;
  onEditResponse?: (responseId: number, responseText: string, interval: string) => Promise<void>;
  onDeleteResponse?: (responseId: number) => Promise<void>;
}

const QuestionDetailSidebar: React.FC<QuestionDetailSidebarProps> = ({
  question,
  step,
  userResponses,
  questionOrder,
  onClose,
  onAddResponse,
  onEditResponse,
  onDeleteResponse
}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  // Response form state
  const [showAddForm, setShowAddForm] = useState(false);
  const [newResponseText, setNewResponseText] = useState('');
  const [newResponseInterval, setNewResponseInterval] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'>('daily');
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Edit response state
  const [editingResponseId, setEditingResponseId] = useState<number | null>(null);
  const [editResponseText, setEditResponseText] = useState('');
  const [editResponseInterval, setEditResponseInterval] = useState<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'>('daily');
  const [deletingResponseId, setDeletingResponseId] = useState<number | null>(null);

  // Trigger animation on mount
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Update new response interval when user responses change
  useEffect(() => {
    const availableIntervals = getAvailableIntervals();
    if (availableIntervals.length > 0 && !availableIntervals.includes(newResponseInterval)) {
      setNewResponseInterval(availableIntervals[0]);
    }
  }, [userResponses.length, newResponseInterval]);

  // Client view helper functions
  const getIntervalColor = (interval: string) => {
    switch (interval) {
      case 'daily': return 'bg-red-500';
      case 'weekly': return 'bg-yellow-500';
      case 'monthly': return 'bg-green-500';
      case 'quarterly': return 'bg-purple-500';
      case 'yearly': return 'bg-blue-500';
      default: return 'bg-gray-500';
    }
  };

  // Get intervals that are already used in existing responses
  const getUsedIntervals = (): Set<string> => {
    return new Set(userResponses.map(r => r.interval));
  };

  // Get available intervals for new responses (excluding already used ones)
  const getAvailableIntervals = (): Array<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'> => {
    const allIntervals: Array<'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly'> = ['daily', 'weekly', 'monthly', 'quarterly', 'yearly'];
    const usedIntervals = getUsedIntervals();
    return allIntervals.filter(interval => !usedIntervals.has(interval));
  };

  const handleClose = () => {
    setIsVisible(false);
    // Wait for animation to complete before calling onClose
    setTimeout(() => {
      onClose();
    }, 300);
  };

  const handleAddResponse = async () => {
    if (newResponseText.trim() && onAddResponse && !isSubmitting && question) {
      setIsSubmitting(true);
      try {
        await onAddResponse(question.id, newResponseText.trim(), newResponseInterval);
        setNewResponseText('');
        setShowAddForm(false);
        const availableIntervals = getAvailableIntervals();
        if (availableIntervals.length > 0) {
          setNewResponseInterval(availableIntervals[0]);
        }
      } catch (error) {
        console.error('Failed to add response:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleEditResponse = (response: UserResponse) => {
    setEditingResponseId(response.id);
    setEditResponseText(response.response_text);
    setEditResponseInterval(response.interval);
  };

  const handleSaveEdit = async () => {
    if (editingResponseId && editResponseText.trim() && onEditResponse && !isSubmitting) {
      setIsSubmitting(true);
      try {
        await onEditResponse(editingResponseId, editResponseText.trim(), editResponseInterval);
        setEditingResponseId(null);
        setEditResponseText('');
        setEditResponseInterval('daily');
      } catch (error) {
        console.error('Failed to save edit:', error);
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  const handleCancelEdit = () => {
    setEditingResponseId(null);
    setEditResponseText('');
    setEditResponseInterval('daily');
  };

  const handleDeleteResponse = async (responseId: number) => {
    if (onDeleteResponse && !deletingResponseId) {
      const result = await Swal.fire({
        title: 'Delete Response',
        text: 'Are you sure you want to delete this response? This action cannot be undone.',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#dc2626',
        cancelButtonColor: '#6b7280',
        confirmButtonText: 'Yes, delete it!',
        cancelButtonText: 'Cancel',
        reverseButtons: true,
        focusCancel: true
      });

      if (result.isConfirmed) {
        setDeletingResponseId(responseId);
        try {
          await onDeleteResponse(responseId);
          await Swal.fire({
            title: 'Deleted!',
            text: 'Your response has been deleted successfully.',
            icon: 'success',
            timer: 2000,
            showConfirmButton: false
          });
        } catch (error) {
          console.error('Failed to delete response:', error);
          await Swal.fire({
            title: 'Error!',
            text: 'Failed to delete the response. Please try again.',
            icon: 'error',
            confirmButtonColor: '#dc2626'
          });
        } finally {
          setDeletingResponseId(null);
        }
      }
    }
  };

  // Handle escape key to close sidebar
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        handleClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  // Handle backdrop click to close sidebar
  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  if (!question || !step) {
    return null;
  }

  return (
    <>
      {/* Gray overlay background */}
      <div 
        className={`fixed inset-0 bg-black z-40 transition-opacity duration-300 ${
          isVisible ? 'opacity-50' : 'opacity-0'
        }`}
        onClick={handleBackdropClick}
      />
      
      {/* Sliding sidebar */}
        <div 
        className={`fixed top-0 right-0 h-full w-[600px] bg-white shadow-xl z-50 transform transition-transform duration-300 ease-in-out ${
            isVisible ? 'translate-x-0' : 'translate-x-full'
        }`}
        >
        <div className="h-full overflow-hidden flex flex-col">
          {/* Header */}
          <div className="bg-gray-50 px-6 py-6 border-b border-gray-200 flex-shrink-0">
            <div className="flex justify-between items-start">
              <div>
                <h3 className="text-lg font-semibold text-gray-700">Step {step.step_number}, Question {questionOrder}</h3>
              </div>
              <button
                onClick={handleClose}
                className="text-gray-400 hover:text-gray-600 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 p-6 overflow-y-auto">
            {/* Question Text */}
            <div className="mb-6">
              <div className="flex items-start pb-4 border-b border-gray-300">
                <div className="w-8 h-8 min-w-8 bg-secondary-500 text-white rounded-full flex items-center justify-center text-sm font-bold mr-3 flex-shrink-0">
                    {questionOrder}
                </div>
                <h3 className="text-gray-700 font-medium">{question.question_text}</h3>
              </div>
            </div>

            {/* My Response section */}
            <div>
              {/* My Response header */}
              <div className="flex items-center justify-between mb-4">
                <span className="text-gray-400 text-sm font-bold">MY RESPONSE</span>
                <span className="bg-gray-300 text-gray-700 text-xs px-2 py-1 rounded-full">
                  {userResponses.length} responses
                </span>
              </div>
              
              {/* Response list */}
              <div className="space-y-3 mb-4">
                {userResponses.map((response) => (
                  <div key={response.id} className="bg-white border border-gray-200 rounded-lg p-3 relative hover:border-secondary-400">
                    {/* Frequency badge */}
                    <div className="flex items-center justify-between mb-2">
                      <span className={`${getIntervalColor(response.interval)} text-white text-xs px-3 py-1 rounded-md`}>
                        {response.interval.charAt(0).toUpperCase() + response.interval.slice(1)}
                      </span>
                      <div className="flex items-center space-x-1">
                        <button
                          onClick={() => handleEditResponse(response)}
                          className="text-gray-600 hover:text-gray-800 text-sm mx-2"
                          title="Edit response"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDeleteResponse(response.id)}
                          disabled={deletingResponseId === response.id}
                          className={`text-sm mx-2 ${
                            deletingResponseId === response.id 
                              ? 'text-gray-400 cursor-not-allowed' 
                              : 'text-gray-600 hover:text-gray-800'
                          }`}
                          title="Delete response"
                        >
                          {deletingResponseId === response.id ? (
                            <div className="w-4 h-4 border border-gray-400 border-t-transparent rounded-full animate-spin"></div>
                          ) : (
                            '🗑️'
                          )}
                        </button>
                      </div>
                    </div>
                    
                    {/* Response text or edit form */}
                    {editingResponseId === response.id ? (
                      <div className="space-y-3 text-sm">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Interval</label>
                          <select
                            value={editResponseInterval}
                            onChange={(e) => setEditResponseInterval(e.target.value as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly')}
                            disabled={true}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none bg-gray-200 text-gray-500 cursor-not-allowed"
                            title="Interval cannot be changed when editing"
                          >
                            <option value={editResponseInterval}>
                              {editResponseInterval.charAt(0).toUpperCase() + editResponseInterval.slice(1)}
                            </option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-1">Your Response</label>
                          <textarea
                            value={editResponseText}
                            onChange={(e) => setEditResponseText(e.target.value)}
                            className="w-full p-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-secondary-500"
                            rows={3}
                          />
                        </div>
                        <div className="flex justify-end space-x-2">
                          <button
                            onClick={handleCancelEdit}
                            className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                          >
                            Cancel
                          </button>
                          <button
                            onClick={handleSaveEdit}
                            disabled={isSubmitting}
                            className={`px-3 py-1 text-sm text-white rounded flex items-center space-x-1 ${
                              isSubmitting 
                                ? 'bg-gray-400 cursor-not-allowed' 
                                : 'bg-green-600 hover:bg-green-700'
                            }`}
                          >
                            {isSubmitting ? (
                              <>
                                <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                                <span>Saving...</span>
                              </>
                            ) : (
                              <>
                                <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                </svg>
                                <span>Save Response</span>
                              </>
                            )}
                          </button>
                        </div>
                      </div>
                    ) : (
                      <p className="text-gray-800 text-sm whitespace-pre-line">{response.response_text}</p>
                    )}
                  </div>
                ))}
              </div>
              
              {/* Add Response section */}
              {!showAddForm ? (
                <div className="flex justify-start">
                  <button
                    onClick={() => {
                      const availableIntervals = getAvailableIntervals();
                      if (availableIntervals.length > 0) {
                        setNewResponseInterval(availableIntervals[0]);
                        setShowAddForm(true);
                      }
                    }}
                    disabled={getAvailableIntervals().length === 0}
                    className={`text-sm border-2 border-dashed rounded-lg py-4 px-4 transition-colors duration-200 flex items-center space-x-2 bg-white ${
                      getAvailableIntervals().length === 0
                        ? 'border-gray-200 text-gray-400 cursor-not-allowed'
                        : 'border-gray-300 text-purple-600 hover:border-secondary-400 hover:bg-secondary-50'
                    }`}
                  >
                    <svg className="w-5 h-5 font-bold" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M19 13h-6v6h-2v-6H5v-2h6V5h2v6h6v2z" stroke="currentColor" strokeWidth="2"/>
                    </svg>
                    <span className={getAvailableIntervals().length === 0 ? 'text-gray-400' : 'text-gray-500'}>
                      {getAvailableIntervals().length === 0 
                        ? 'All intervals used' 
                        : userResponses.length > 0 ? 'Add Another Response' : 'Add Response'
                      }
                    </span>
                  </button>
                </div>
              ) : getAvailableIntervals().length > 0 ? (
                <div className="bg-white border border-gray-300 rounded-lg p-4 text-sm">
                  <div className="mb-2">
                    <p className="text-xs text-gray-600">
                      Available intervals: {getAvailableIntervals().join(', ')}
                    </p>
                  </div>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tracking Interval</label>
                      <select
                        value={newResponseInterval}
                        onChange={(e) => setNewResponseInterval(e.target.value as 'daily' | 'weekly' | 'monthly' | 'quarterly' | 'yearly')}
                        className="w-full p-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-secondary-500 bg-gray-100"
                      >
                        {getAvailableIntervals().map(interval => (
                          <option key={interval} value={interval}>
                            {interval.charAt(0).toUpperCase() + interval.slice(1)}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Your Response</label>
                      <textarea
                        value={newResponseText}
                        onChange={(e) => setNewResponseText(e.target.value)}
                        placeholder="Enter your response..."
                        className="w-full p-2 border border-gray-300 rounded-sm focus:outline-none focus:ring-2 focus:ring-secondary-500"
                        rows={3}
                      />
                    </div>
                    <div className="flex justify-end space-x-2">
                      <button
                        onClick={() => {
                          setShowAddForm(false);
                          setNewResponseText('');
                          const availableIntervals = getAvailableIntervals();
                          setNewResponseInterval(availableIntervals.length > 0 ? availableIntervals[0] : 'daily');
                        }}
                        className="px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-50"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleAddResponse}
                        disabled={isSubmitting}
                        className={`px-3 py-1 text-sm text-white rounded flex items-center space-x-1 ${
                          isSubmitting 
                            ? 'bg-gray-400 cursor-not-allowed' 
                            : 'bg-green-600 hover:bg-green-700'
                        }`}
                      >
                        {isSubmitting ? (
                          <>
                            <div className="w-3 h-3 border border-white border-t-transparent rounded-full animate-spin"></div>
                            <span>Saving...</span>
                          </>
                        ) : (
                          <>
                            <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                            </svg>
                            <span>Save Response</span>
                          </>
                        )}
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                /* No available intervals message */
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 text-center">
                  <p className="text-sm text-gray-500">
                    All interval types have been used. Delete an existing response to add a new one with that interval.
                  </p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default QuestionDetailSidebar;